import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import SpaceBetween from "@cloudscape-design/components/space-between"
import type { DTADetails, SigningRecord, VtmfFilingJob } from "../../../services/dtaApi"
import ApprovalList from "../../../components/Workflow/ApprovalList"
import WorkflowDiagram from "../../../components/Workflow/WorkflowDiagram"
import ActivityTimeline from "./ActivityTimeline"

interface OverviewPanelProps {
  stream: DTADetails
  dtaId: string
  /**
   * Version shown.
   */
  selectedVersion: string
  /**
   * Stage shown, which differs from stream.workflow_stage for older versions.
   */
  displayStage: string | undefined
  signingRecords: SigningRecord[]
  filings: VtmfFilingJob[]
  /**
   * Refetch after an approval action.
   */
  onRefresh: () => void
  /**
   * Switch to the Audit Trail tab from the "View audit trail" link.
   */
  onViewAuditTrail: () => void
}

/**
 * Overview tab: workflow diagram, agreement details, approvals and signatures, and recent activity.
 */
const OverviewPanel = ({
  stream,
  dtaId,
  selectedVersion,
  displayStage,
  signingRecords,
  filings,
  onRefresh,
  onViewAuditTrail,
}: OverviewPanelProps) => {
  return (
    <SpaceBetween size="l">
      {/* Workflow Diagram (third-party only) */}
      {
        <Container header={<Header variant="h2">Workflow</Header>}>
          <WorkflowDiagram
            dtaId={dtaId}
            workflowStage={displayStage}
            statusHistory={stream.status_history}
          />
        </Container>
      }

      <Container header={<Header variant="h2">Agreement Details</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Study</Box>
            <div>{stream.study_id}</div>
          </div>
          {
            <div>
              <Box variant="awsui-key-label">Vendor</Box>
              <div>{stream.vendor_name}</div>
            </div>
          }
          <div>
            <Box variant="awsui-key-label">Created</Box>
            <div>{new Date(stream.created_at).toLocaleDateString()}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Version</Box>
            <div>{selectedVersion}</div>
          </div>
          {stream.data_stream_id ? (
            <div>
              <Box variant="awsui-key-label">Data Stream ID</Box>
              <div>
                <code>{stream.data_stream_id}</code>
              </div>
            </div>
          ) : null}
          {stream.document_content?.data_purpose ? (
            <div>
              <Box variant="awsui-key-label">Purpose</Box>
              <div>
                {typeof stream.document_content.data_purpose === "string"
                  ? stream.document_content.data_purpose
                  : JSON.stringify(stream.document_content.data_purpose)}
              </div>
            </div>
          ) : null}
        </ColumnLayout>
      </Container>

      <ColumnLayout columns={2}>
        {
          <Container header={<Header variant="h2">Approvals</Header>}>
            <SpaceBetween size="m">
              <ApprovalList
                dtaId={dtaId}
                approvals={stream.approvals}
                onApprovalUpdate={onRefresh}
              />

              {signingRecords.length > 0 && (
                <SpaceBetween size="xs">
                  <Box variant="h3">Signatures</Box>
                  {signingRecords.map((record) => (
                    <Box key={record.signing_record_id}>
                      <Box variant="strong">{record.stage}</Box>
                      {record.evidence?.map((ev, i) => (
                        <Box key={i} variant="small" color="text-body-secondary">
                          <Icon name="check" size="normal" /> {ev.signer_role} •{" "}
                          {new Date(ev.signed_at).toLocaleDateString()}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </SpaceBetween>
              )}

              {filings.length > 0 && (
                <SpaceBetween size="xs">
                  <Box variant="h3">vTMF Filing</Box>
                  {filings.map((f) => (
                    <Box key={f.filing_job_id}>
                      <span>{f.status}</span> {f.vtmf_reference && <code>{f.vtmf_reference}</code>}
                    </Box>
                  ))}
                </SpaceBetween>
              )}
            </SpaceBetween>
          </Container>
        }

        <Container
          header={
            <Header
              variant="h2"
              actions={
                <Link variant="primary" onFollow={onViewAuditTrail}>
                  View audit trail →
                </Link>
              }
            >
              Recent activity
            </Header>
          }
        >
          <ActivityTimeline
            statusHistory={stream.status_history}
            limit={3}
            ariaLabel="Recent DTA activity"
          />
        </Container>
      </ColumnLayout>
    </SpaceBetween>
  )
}

export default OverviewPanel
